import {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type FC,
	type ReactElement,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useReadOnlyAccess } from "../hooks/useReadOnlyAccess";
import { LitheSliceActions } from "../redux/lithe/lithe.slice";
import { fetchLitheThunk } from "../redux/lithe/lithe.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";
import { services } from "../services";
import { AnalysisStageEnum } from "../types/analysis.type";
import {
	LitheStepEnum,
	LitheCreationTypeEnum,
} from "../types/lithe.type";
import {
	get_all_steps,
	get_current_step,
	getLitheErrorMessage,
	getLitheStepLabel,
	getIncompleteSpecsQuestions,
} from "../utils/litheUtils";
import { getRawProbStatus } from "../utils/rawProbUtils";
import { toCurrentTenantPath } from "../utils/tenantUtils";

export const LitheContext = createContext<any>(null);

const datasetLitheStepsReferenceObj = {
	[LitheStepEnum.SPECS]: {
		index: 0,
		stepId: LitheStepEnum.SPECS,
		name: "Extract Data",
		isCompleted: true,
	},
	[LitheStepEnum.DATAVIZ]: {
		index: 1,
		stepId: LitheStepEnum.DATAVIZ,
		name: "Proxy Data",
		isCompleted: true,
	},
	[LitheStepEnum.ENGGFEATURES]: {
		index: 2,
		stepId: LitheStepEnum.ENGGFEATURES,
		name: "Engg. Features",
		isCompleted: true,
	},
	[LitheStepEnum.TRAININGDATA]: {
		index: 3,
		stepId: LitheStepEnum.TRAININGDATA,
		name: "Training Data",
		isCompleted: true,
	},
	[LitheStepEnum.TRANSITION]: {
		index: 4,
		stepId: LitheStepEnum.TRANSITION,
		name: "Transition Config",
		isCompleted: true,
	},
	[LitheStepEnum.REPLINES]: {
		index: 5,
		stepId: LitheStepEnum.REPLINES,
		name: "Replines",
		isCompleted: true,
	},
	[LitheStepEnum.RFSPECS]: {
		index: 6,
		stepId: LitheStepEnum.RFSPECS,
		name: "Model Params",
		isCompleted: true,
	},
	[LitheStepEnum.APPROVAL]: {
		index: 7,
		stepId: LitheStepEnum.APPROVAL,
		name: "Approval",
		isCompleted: false,
	},
	[LitheStepEnum.LITHEAPPROVED]: {
		index: 8,
		stepId: LitheStepEnum.LITHEAPPROVED,
		name: "Lithe Approved",
		isCompleted: false,
	},
};

const userGuidedLitheStepsReferenceObj = {
	[LitheStepEnum.SPECS]: {
		index: 0,
		stepId: LitheStepEnum.SPECS,
		name: "Extract Data",
		isCompleted: true,
	},
	[LitheStepEnum.UGCLEANDATA]: {
		index: 1,
		stepId: LitheStepEnum.UGCLEANDATA,
		name: "Clean Data",
		isCompleted: true,
	},
	[LitheStepEnum.UGFILTERS]: {
		index: 2,
		stepId: LitheStepEnum.UGFILTERS,
		name: "Loan Filters",
		isCompleted: true,
	},
	[LitheStepEnum.UGTRANSDEF]: {
		index: 3,
		stepId: LitheStepEnum.UGTRANSDEF,
		name: "Transition Definition",
		isCompleted: true,
	},
	[LitheStepEnum.UGFEATUREENG]: {
		index: 4,
		stepId: LitheStepEnum.UGFEATUREENG,
		name: "Engineering Features",
		isCompleted: true,
	},
	[LitheStepEnum.UGREPLINES]: {
		index: 5,
		stepId: LitheStepEnum.UGREPLINES,
		name: "Risk Replines",
		isCompleted: true,
	},
	[LitheStepEnum.RFSPECS]: {
		index: 6,
		stepId: LitheStepEnum.RFSPECS,
		name: "Probability Analysis",
		isCompleted: true,
	},
	[LitheStepEnum.APPROVAL]: {
		index: 7,
		stepId: LitheStepEnum.APPROVAL,
		name: "Approval",
		isCompleted: false,
	},
};

const manualLitheStepsReferenceObj = {
	[LitheStepEnum.SPECS]: {
		index: 0,
		stepId: LitheStepEnum.SPECS,
		name: "Extract Data",
		isCompleted: true,
	},
	[LitheStepEnum.RFSPECS]: {
		index: 1,
		stepId: LitheStepEnum.RFSPECS,
		name: "Risk Factor",
		isCompleted: true,
	},
	[LitheStepEnum.RAWPROBS]: {
		index: 2,
		stepId: LitheStepEnum.RAWPROBS,
		name: "Probability",
		isCompleted: true,
	},
	[LitheStepEnum.APPROVAL]: {
		index: 3,
		stepId: LitheStepEnum.APPROVAL,
		name: "Approval",
		isCompleted: false,
	},
	[LitheStepEnum.LITHEAPPROVED]: {
		index: 4,
		stepId: LitheStepEnum.LITHEAPPROVED,
		name: "Lithe Approved",
		isCompleted: false,
	},
};

const programmaticLitheStepsReferenceObj = {
	[LitheStepEnum.SPECS]: {
		index: 0,
		stepId: LitheStepEnum.SPECS,
		name: "Extract Data",
		isCompleted: true,
	},
	[LitheStepEnum.APPROVAL]: {
		index: 3,
		stepId: LitheStepEnum.APPROVAL,
		name: "Approval",
		isCompleted: false,
	},
	[LitheStepEnum.LITHEAPPROVED]: {
		index: 4,
		stepId: LitheStepEnum.LITHEAPPROVED,
		name: "Lithe Approved",
		isCompleted: false,
	},
};

export const LitheContextProvider: FC<{
	children: ReactElement;
}> = ({ children }) => {
	const { method, step, litheId } = useParams();
	const navigate = useNavigate();
	const isReadOnlyUser = useReadOnlyAccess();

	const dispatch = useAppDispatch();

	const {
		meta,
		rawProbData,
		rfSpecsQuestions,
		specsQuestions,
	} = useAppSelector((state) => state.lithe);

	const [
		continueAndConfirmLoading,
		setContinueAndConfirmLoading,
	] = useState(false);
	const [stepScreenLoading, setStepScreenLoading] =
		useState(false);
	const [stepScreenStatus, setStepScreenStatus] = useState<
		"idle" | "loading" | "success" | "empty" | "error"
	>("idle");
	const incompleteSpecsQuestions = useMemo(() => {
		return getIncompleteSpecsQuestions(
			specsQuestions.data ?? []
		);
	}, [specsQuestions.data]);
	const incompleteManualRFQuestions = useMemo(() => {
		return getIncompleteSpecsQuestions(
			rfSpecsQuestions.data ?? []
		);
	}, [rfSpecsQuestions.data]);
	const litheCreationType = (meta.data?.litheCreationType ??
		method) as LitheCreationTypeEnum;
	const availableLitheSteps = useMemo(() => {
		const backendSteps = meta.data?.allSteps ?? [];
		return get_all_steps(litheCreationType, backendSteps);
	}, [litheCreationType, meta.data?.allSteps]);
	const effectiveCurrentStep = useMemo(() => {
		return (
			meta.data?.currentStep ??
			((availableLitheSteps.includes(step as LitheStepEnum)
				? (step as LitheStepEnum)
				: null) as LitheStepEnum | null)
		);
	}, [availableLitheSteps, meta.data?.currentStep, step]);

	const allLitheSteps = useMemo(() => {
		if (!availableLitheSteps.length) {
			return [];
		}
		const currentStepFromMeta = effectiveCurrentStep;
		const availableSteps = availableLitheSteps;
		const hasResolvedLiveCurrentStep = Boolean(
			meta.data?.currentStep
		);

		const backendUnlockedStep =
			currentStepFromMeta === LitheStepEnum.LITHEAPPROVED
				? availableSteps[availableSteps.length - 1]
				: (currentStepFromMeta as LitheStepEnum);
		const routeStep = step as LitheStepEnum | undefined;
		const routeStepIndex = routeStep
			? availableSteps.indexOf(routeStep)
			: -1;
		const unlockedIndex = hasResolvedLiveCurrentStep
			? Math.max(
					availableSteps.indexOf(backendUnlockedStep),
					0
				)
			: routeStepIndex >= 0
				? routeStepIndex
				: 0;
		const activeStepForUi =
			routeStepIndex >= 0 &&
			(!hasResolvedLiveCurrentStep ||
				routeStepIndex <= unlockedIndex)
				? routeStep
				: availableSteps[unlockedIndex];

		// const referenceObj: any =
		// 	method === "dataset"
		// 		? datasetLitheStepsReferenceObj
		// 		: manualLitheStepsReferenceObj;

		const referenceObj: Record<string, any> =
			litheCreationType === LitheCreationTypeEnum.DATASET
				? datasetLitheStepsReferenceObj
				: litheCreationType === LitheCreationTypeEnum.UG
					? userGuidedLitheStepsReferenceObj
					: litheCreationType ===
						  LitheCreationTypeEnum.MANUAL
						? manualLitheStepsReferenceObj
						: programmaticLitheStepsReferenceObj;

		const steps = availableSteps.map(
			(step: LitheStepEnum, index: number) => {
				const res = {
					...(referenceObj[step] ?? {
						index,
						stepId: step,
						name: getLitheStepLabel(
							step,
							litheCreationType
						),
					}),
					isCurrent: false,
					isCompleted: false,
					isAccessible: false,
					isLocked: false,
				};
				if (step === activeStepForUi) {
					res.isCurrent = true;
				}
				if (
					index < unlockedIndex ||
					(index === unlockedIndex &&
						currentStepFromMeta ===
							LitheStepEnum.LITHEAPPROVED)
				) {
					res.isCompleted = true;
				}
				if (index <= unlockedIndex) {
					res.isAccessible = true;
				} else {
					res.isLocked = true;
				}

				if (
					step === LitheStepEnum.APPROVAL &&
					currentStepFromMeta ===
						LitheStepEnum.LITHEAPPROVED
				) {
					res.name = "Lithe Approved";
				}

				return res;
			}
		);
		return steps;
	}, [
		availableLitheSteps,
		effectiveCurrentStep,
		litheCreationType,
		meta.data?.currentStep,
		step,
	]);

	useEffect(() => {
		if (!allLitheSteps.length || !litheId || !method) {
			return;
		}

		if (!meta.data?.currentStep) {
			return;
		}

		const requestedStep = allLitheSteps.find(
			(stepObj: any) => stepObj.stepId === step
		);
		const activeStep = allLitheSteps.find(
			(stepObj: any) => stepObj.isCurrent
		);

		if (
			activeStep &&
			!requestedStep?.isAccessible &&
			activeStep.stepId !== step
		) {
			navigate(
				toCurrentTenantPath(`/lithe/${method}/${activeStep.stepId}/${litheId}`),
				{ replace: true }
			);
		}
	}, [
		allLitheSteps,
		litheId,
		meta.data?.currentStep,
		method,
		navigate,
		step,
	]);

	const moveToStep = useCallback(
		(step: any) => {
			if (!step?.isAccessible) {
				return;
			}
			dataVizContinuePolling.current = false;
			cashflowVizContinuePolling.current = false;
			probsContinuePolling.current = false;
			navigate(
				toCurrentTenantPath(`/lithe/${method}/${step.stepId}/${litheId}`)
			);
		},
		[litheId, method, navigate]
	);

	const moveToNextStep = useCallback(
		(currentStep?: LitheStepEnum) => {
			const list = availableLitheSteps;
			if (!list.length) return;

			const stepToMoveFrom = get_current_step({
				all_steps: list,
				route_step: currentStep,
				meta_current_step:
					effectiveCurrentStep ?? undefined,
			});

			const currentIndex = list.indexOf(stepToMoveFrom);
			if (
				currentIndex < 0 ||
				currentIndex >= list.length - 1
			) {
				return;
			}
			const nextStep = list[currentIndex + 1];
			navigate(toCurrentTenantPath(`/lithe/${method}/${nextStep}/${litheId}`));
		},
		[
			availableLitheSteps,
			effectiveCurrentStep,
			litheId,
			method,
			navigate,
		]
	);

	const [
		showGenerateSummaryModal,
		setShowGenerateSummaryModal,
	] = useState(false);

	const processBucketSelectionChanges =
		useCallback(() => {}, []);

	const isSuccessfulStepResponse = useCallback(
		(response: any): boolean => {
			if (!response) {
				return false;
			}

			if (
				response?.is_success === false ||
				response?.success === false
			) {
				return false;
			}

			return (
				String(response?.status ?? "")
					.trim()
					.toUpperCase() !== "ERROR"
			);
		},
		[]
	);

	const isLitheApproved = useMemo(() => {
		return (
			effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
		);
	}, [effectiveCurrentStep]);

	const dataVizPollingController = useRef<any>({});
	const dataVizPollingTimeout =
		useRef<NodeJS.Timeout | null>(null);
	const dataVizContinuePolling = useRef(true);

	const handleDataVizPolling = useCallback(
		async (litheId: string) => {
			if (!litheId) return;
			try {
				dispatch(
					LitheSliceActions.updateLitheDataVizResults(null)
				);
				dispatch(
					LitheSliceActions.handleDataVizResultsLoading(
						true
					)
				);
				const res =
					await services.litheService.getLitheDataVizResults(
						litheId,
						{
							signal:
								dataVizPollingController.current[litheId]
									.signal,
						}
					);
				if (res.status === AnalysisStageEnum.COMPLETED) {
					dispatch(
						LitheSliceActions.updateLitheDataVizResults(res)
					);
					dispatch(
						LitheSliceActions.handleDataVizResultsLoading(
							false
						)
					);
				}
				if (res.status === AnalysisStageEnum.ERROR) {
					dispatch(
						LitheSliceActions.updateLitheDataVizResults(
							null
						)
					);
					dispatch(
						LitheSliceActions.handleDataVizResultsLoading(
							false
						)
					);
					dispatch(
						LitheSliceActions.updateLitheDataVizResultsError(
							res.output
						)
					);
					toast.error(
						`Error filtering training data: ${
							res.output.error_msg.error_message
						}`,
						{
							autoClose: 10000,
						}
					);
					dataVizContinuePolling.current = false;
				} else if (
					res.status === AnalysisStageEnum.NOT_YET_STARTED
				) {
					dispatch(
						LitheSliceActions.updateLitheDataVizResults(res)
					);
					dispatch(
						LitheSliceActions.handleDataVizResultsLoading(
							false
						)
					);
					dataVizContinuePolling.current = false;
				} else if (
					res.status !== AnalysisStageEnum.COMPLETED
				) {
					dataVizPollingTimeout.current = setTimeout(() => {
						dataVizContinuePolling.current = true;
						void handleDataVizPolling(litheId);
					}, 15000);
				} else {
					dataVizContinuePolling.current = false;
				}
			} catch {
				dataVizContinuePolling.current = false;
				dispatch(
					LitheSliceActions.handleDataVizResultsLoading(
						false
					)
				);
				const signal =
					dataVizPollingController.current[litheId].signal;
				if (signal.aborted) return;
				toast.error("Error filtering training data");
			}
		},
		[dispatch]
	);

	useEffect(() => {
		if (!litheId) return undefined;
		const controller = new AbortController();
		dataVizPollingController.current[litheId] = controller;
		return () => {
			controller.abort();
			clearTimeout(
				dataVizPollingTimeout.current as NodeJS.Timeout
			);
		};
	}, [litheId]);

	const abortSteps = useCallback(() => {
		if (litheId) {
			if (
				dataVizPollingController.current[litheId] &&
				step !== LitheStepEnum.DATAVIZ
			) {
				dataVizPollingController.current[litheId].abort();
				clearTimeout(
					dataVizPollingTimeout.current as NodeJS.Timeout
				);
				dispatch(LitheSliceActions.resetLitheDataVizStep());
			}
			if (
				cashflowVizPollingController.current[litheId] &&
				step !== LitheStepEnum.CASHFLOWVIZ
			) {
				cashflowVizPollingController.current[
					litheId
				].abort();
				clearTimeout(
					cashflowVizPollingTimeout.current as NodeJS.Timeout
				);
				dispatch(
					LitheSliceActions.resetLitheCashflowVizStep()
				);
			}
			if (
				probsPollingController.current[litheId] &&
				step !== LitheStepEnum.PROBS
			) {
				probsPollingController.current[litheId].abort();
				dispatch(LitheSliceActions.resetLitheProbsStep());
			}

			if (step !== LitheStepEnum.RAWPROBS) {
				dispatch(LitheSliceActions.resetLitheRawProbStep());
			}

			if (step !== LitheStepEnum.RFSPECS) {
				dispatch(LitheSliceActions.resetLitheRFSpecsStep());
			}

			if (step !== LitheStepEnum.PROBS) {
				dispatch(LitheSliceActions.resetLitheProbsStep());
			}
		}
	}, [dispatch, litheId, step]);

	useEffect(() => {
		return () => {
			abortSteps();
		};
	}, [abortSteps, step]);

	const triggerDataVizPolling = useCallback(
		(newLitheId: string) => {
			if (litheId) {
				abortSteps();
				if (dataVizPollingController.current[litheId]) {
					dataVizPollingController.current[litheId].abort();
					clearTimeout(
						dataVizPollingTimeout.current as NodeJS.Timeout
					);
				}
				dataVizPollingController.current[newLitheId] =
					new AbortController();
				dataVizPollingTimeout.current = null;
				dataVizContinuePolling.current = true;
				void handleDataVizPolling(newLitheId);
			}
		},
		[abortSteps, handleDataVizPolling, litheId]
	);

	const cashflowVizPollingController = useRef<any>({});
	const cashflowVizPollingTimeout =
		useRef<NodeJS.Timeout | null>(null);
	const cashflowVizContinuePolling = useRef(true);

	const handleCashflowVizPolling = useCallback(
		async (litheId: string) => {
			if (!litheId) return;
			try {
				dispatch(
					LitheSliceActions.updateCashflowVizResults(null)
				);
				dispatch(
					LitheSliceActions.handleCashflowVizResultsLoading(
						true
					)
				);
				const res =
					await services.litheService.getLitheCashflowVizResults(
						litheId,
						{
							signal:
								cashflowVizPollingController.current[
									litheId
								].signal,
						}
					);
				if (res.status === AnalysisStageEnum.COMPLETED) {
					dispatch(
						LitheSliceActions.updateCashflowVizResults(res)
					);
					dispatch(
						LitheSliceActions.handleCashflowVizResultsLoading(
							false
						)
					);
				}
				if (res.status === AnalysisStageEnum.ERROR) {
					dispatch(
						LitheSliceActions.updateCashflowVizResults(null)
					);
					dispatch(
						LitheSliceActions.handleCashflowVizResultsLoading(
							false
						)
					);
					dispatch(
						LitheSliceActions.updateLitheCashflowVizResultsError(
							res.output
						)
					);
					toast.error(
						`Error filtering training data: ${
							res.output.error_msg.error_message
						}`,
						{
							autoClose: 10000,
						}
					);
					cashflowVizContinuePolling.current = false;
				} else if (
					res.status === AnalysisStageEnum.NOT_YET_STARTED
				) {
					dispatch(
						LitheSliceActions.updateCashflowVizResults(res)
					);
					dispatch(
						LitheSliceActions.handleCashflowVizResultsLoading(
							false
						)
					);
					cashflowVizContinuePolling.current = false;
				} else if (
					res.status !== AnalysisStageEnum.COMPLETED
				) {
					cashflowVizPollingTimeout.current = setTimeout(
						() => {
							cashflowVizContinuePolling.current = true;
							void handleCashflowVizPolling(litheId);
						},
						15000
					);
				} else {
					cashflowVizContinuePolling.current = false;
				}
			} catch {
				cashflowVizContinuePolling.current = false;
				dispatch(
					LitheSliceActions.handleCashflowVizResultsLoading(
						false
					)
				);
				const signal =
					cashflowVizPollingController.current[litheId]
						.signal;
				if (signal.aborted) return;
				toast.error("Error filtering data");
			}
		},
		[dispatch]
	);

	useEffect(() => {
		if (!litheId) return undefined;
		const controller = new AbortController();
		cashflowVizPollingController.current[litheId] =
			controller;
		return () => {
			controller.abort();
			clearTimeout(
				cashflowVizPollingTimeout.current as NodeJS.Timeout
			);
		};
	}, [litheId]);

	const triggerCashflowVizPolling = useCallback(
		(newLitheId: string) => {
			if (litheId) {
				abortSteps();
				if (cashflowVizPollingController.current[litheId]) {
					cashflowVizPollingController.current[
						litheId
					].abort();
					clearTimeout(
						cashflowVizPollingTimeout.current as NodeJS.Timeout
					);
				}
				cashflowVizPollingController.current[newLitheId] =
					new AbortController();
				cashflowVizPollingTimeout.current = null;
				cashflowVizContinuePolling.current = true;
				void handleCashflowVizPolling(newLitheId);
			}
		},
		[abortSteps, handleCashflowVizPolling, litheId]
	);

	const probsPollingController = useRef<any>({});
	const probsPollingTimeout = useRef<NodeJS.Timeout | null>(
		null
	);
	const probsContinuePolling = useRef(true);

	const handleProbsPolling = useCallback(
		async (litheId: string) => {
			if (!litheId) return;
			try {
				dispatch(
					LitheSliceActions.updateProbsResults(null)
				);
				dispatch(
					LitheSliceActions.handleProbsResultsLoading(true)
				);
				const res =
					await services.litheService.getLitheProbsResults(
						litheId,
						{
							signal:
								probsPollingController.current[litheId]
									.signal,
						}
					);
				if (res.status === AnalysisStageEnum.COMPLETED) {
					dispatch(
						LitheSliceActions.updateProbsResults(res)
					);
					dispatch(
						LitheSliceActions.handleProbsResultsLoading(
							false
						)
					);
				}
				if (res.status === AnalysisStageEnum.ERROR) {
					dispatch(
						LitheSliceActions.updateProbsResults(null)
					);
					dispatch(
						LitheSliceActions.handleProbsResultsLoading(
							false
						)
					);
					dispatch(
						LitheSliceActions.updateProbsResultsError(
							res.output
						)
					);
					toast.error(
						`Error generating probabilities: ${
							res.output.error_msg.error_message
						}`,
						{
							autoClose: 10000,
						}
					);
					probsContinuePolling.current = false;
				} else if (
					res.status !== AnalysisStageEnum.COMPLETED
				) {
					probsPollingTimeout.current = setTimeout(() => {
						probsContinuePolling.current = true;
						void handleProbsPolling(litheId);
					}, 15000);
				} else {
					probsContinuePolling.current = false;
				}
			} catch {
				probsContinuePolling.current = false;
				dispatch(
					LitheSliceActions.handleProbsResultsLoading(false)
				);
				const signal =
					probsPollingController.current[litheId].signal;
				if (signal.aborted) return;

				toast.error("Error generating probabilities");
			}
		},
		[dispatch]
	);

	const getProbResultOnDemand = useCallback(async () => {
		if (!litheId) return;
		try {
			dispatch(LitheSliceActions.updateProbsResults(null));
			dispatch(
				LitheSliceActions.handleProbsResultsLoading(true)
			);
			const res =
				await services.litheService.getLitheProbsResults(
					litheId
				);
			if (res.status === AnalysisStageEnum.COMPLETED) {
				dispatch(LitheSliceActions.updateProbsResults(res));
				dispatch(
					LitheSliceActions.handleProbsResultsLoading(false)
				);
			}
			if (res.status === AnalysisStageEnum.ERROR) {
				dispatch(
					LitheSliceActions.updateProbsResults(null)
				);
				dispatch(
					LitheSliceActions.handleProbsResultsLoading(false)
				);
				dispatch(
					LitheSliceActions.updateProbsResultsError(
						res.output
					)
				);
				toast.error(
					`Error generating probabilities: ${
						res.output.error_msg.error_message
					}`,
					{
						autoClose: 10000,
					}
				);
			} else if (
				res.status !== AnalysisStageEnum.COMPLETED
			) {
				// do nothing
				toast.info(
					"Probabilities generation still in progress"
				);
			}
		} catch {
			toast.error("Error generating probabilities");
		}
	}, [dispatch, litheId]);

	useEffect(() => {
		if (!litheId) return undefined;
		const controller = new AbortController();
		probsPollingController.current[litheId] = controller;
		return () => {
			controller.abort();
			clearTimeout(
				probsPollingTimeout.current as NodeJS.Timeout
			);
		};
	}, [litheId]);

	const triggerProbsPolling = useCallback(
		(newLitheId: string) => {
			if (litheId) {
				abortSteps();
				if (probsPollingController.current[litheId]) {
					probsPollingController.current[litheId].abort();
					clearTimeout(
						probsPollingTimeout.current as NodeJS.Timeout
					);
				}
				probsPollingController.current[newLitheId] =
					new AbortController();
				probsPollingTimeout.current = null;
				probsContinuePolling.current = true;
				void handleProbsPolling(newLitheId);
			}
		},
		[abortSteps, handleProbsPolling, litheId]
	);

	const processDataVizChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			toast.success("Data Filtering started");
			dispatch(
				LitheSliceActions.handleDataVizResultsLoading(true)
			);
			const data =
				await services.litheService.processDataViz(litheId);

			if (data.is_success) {
				triggerDataVizPolling(litheId);
			} else {
				toast.error("Error processing training data");
				dispatch(
					LitheSliceActions.handleDataVizResultsLoading(
						false
					)
				);
			}
		} catch {
			toast.error("Error processing training data");
			dispatch(
				LitheSliceActions.handleDataVizResultsLoading(false)
			);
		}
	}, [dispatch, litheId, triggerDataVizPolling]);

	const processCashflowVizChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				toast.success("Data Filtering started");
				dispatch(
					LitheSliceActions.handleCashflowVizResultsLoading(
						true
					)
				);
				const data =
					await services.litheService.processCashflowViz(
						litheId
					);

				if (data.is_success) {
					triggerCashflowVizPolling(litheId);
				} else {
					toast.error("Error processing risk factor");
					dispatch(
						LitheSliceActions.handleCashflowVizResultsLoading(
							false
						)
					);
				}
			} catch {
				toast.error("Error processing risk factor");
				dispatch(
					LitheSliceActions.handleCashflowVizResultsLoading(
						false
					)
				);
			}
		}, [dispatch, litheId, triggerCashflowVizPolling]);

	const processProbsChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			toast.success("Probabilities generation started");
			const data =
				await services.litheService.confirmProbs(litheId);

			if (isSuccessfulStepResponse(data)) {
				return {
					moveToNextStep: true,
				};
			} else {
				return {
					moveToNextStep: false,
				};
			}
		} catch {
			toast.error("Error processing buckets");
			return {
				moveToNextStep: false,
			};
		}
	}, [isSuccessfulStepResponse, litheId]);

	const processRawProbsChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			toast.success("Processing probabilities");

			const pollingStatuses = new Set([
				AnalysisStageEnum.IN_PROGRESS,
				AnalysisStageEnum.NOT_YET_STARTED,
				"PENDING",
				"QUEUED",
				"STARTED",
			]);

			const waitForRawProbCompletion = async () => {
				for (let attempt = 0; attempt < 24; attempt += 1) {
					const result =
						await services.litheService.getLitheRawProbData(
							litheId
						);
					const resultStatus = getRawProbStatus(result);

					if (
						resultStatus === AnalysisStageEnum.COMPLETED
					) {
						return result;
					}

					if (resultStatus === AnalysisStageEnum.ERROR) {
						throw new Error(
							getLitheErrorMessage(
								result?.output?.error_msg ??
									result?.error_msg,
								"Probability processing failed."
							)
						);
					}

					if (
						!pollingStatuses.has(resultStatus) ||
						attempt === 23
					) {
						throw new Error(
							"Probability processing timed out."
						);
					}

					await new Promise<void>((resolve) => {
						setTimeout(resolve, 2500);
					});
				}

				throw new Error(
					"Probability processing timed out."
				);
			};

			const confirmResponse =
				await services.litheService.processRawProbs(
					litheId
				);
			let confirmStatus = getRawProbStatus(confirmResponse);

			if (confirmStatus === AnalysisStageEnum.ERROR) {
				throw new Error(
					getLitheErrorMessage(
						confirmResponse?.output?.error_msg ??
							confirmResponse?.error_msg,
						"Probabilities could not be confirmed."
					)
				);
			}

			if (pollingStatuses.has(confirmStatus)) {
				const completedResult =
					await waitForRawProbCompletion();
				confirmStatus = getRawProbStatus(completedResult);
			}

			if (confirmStatus !== AnalysisStageEnum.COMPLETED) {
				throw new Error(
					"Probabilities could not be confirmed."
				);
			}

			toast.success("Probabilities confirmed successfully");
			return {
				moveToNextStep: true,
			};
		} catch (error) {
			toast.error(
				getLitheErrorMessage(
					error,
					"Error processing probabilities."
				)
			);
			return {
				moveToNextStep: false,
			};
		}
	}, [litheId]);

	const confirmSpecsChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			if (incompleteSpecsQuestions.length > 0) {
				toast.error(
					`Complete the required fields before continuing: ${incompleteSpecsQuestions
						.map((question) => question.question)
						.join(", ")}`
				);
				return {
					moveToNextStep: false,
				};
			}
			toast.success("Metadata confirmed");
			const data =
				await services.litheService.confirmSpecs(litheId);

			if (isSuccessfulStepResponse(data)) {
				// Return success from here then call meta and then move to next
				return {
					moveToNextStep: true,
				};
			} else {
				return {
					moveToNextStep: false,
				};
			}
		} catch {
			toast.error("Error confirming metadata");
			return {
				moveToNextStep: false,
			};
		}
	}, [
		incompleteSpecsQuestions,
		isSuccessfulStepResponse,
		litheId,
	]);

	const confirmDataVizChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			const data =
				await services.litheService.confirmProxyData(
					litheId
				);

			if (isSuccessfulStepResponse(data)) {
				toast.success("Proxy data confirmed successfully");
				return {
					moveToNextStep: true,
				};
			} else {
				return {
					moveToNextStep: false,
				};
			}
		} catch (error) {
			console.error(error);
			toast.error(
				getLitheErrorMessage(
					error,
					"Error confirming proxy data"
				)
			);
			return {
				moveToNextStep: false,
			};
		}
	}, [isSuccessfulStepResponse, litheId]);

	const confirmUgAnalysisStep = useCallback(
		async (ugStep: LitheStepEnum) => {
			try {
				if (!litheId) {
					return { moveToNextStep: false };
				}

				let data: any;
				switch (ugStep) {
					case LitheStepEnum.UGCLEANDATA:
						data =
							await services.litheService.processLitheCleanData(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGFILTERS:
						data =
							await services.litheService.processLitheFilters(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGTRANSDEF:
						data =
							await services.litheService.processLitheTransDef(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGFEATUREENG:
						data =
							await services.litheService.processLitheUgFeatureEngineering(
								litheId,
								true
							);
						break;
					case LitheStepEnum.UGREPLINES:
						data =
							await services.litheService.processLitheUgReplines(
								litheId,
								true
							);
						break;
					default:
						return { moveToNextStep: false };
				}

				if (isSuccessfulStepResponse(data)) {
					toast.success(
						`${getLitheStepLabel(ugStep, LitheCreationTypeEnum.UG)} confirmed successfully`
					);
					return { moveToNextStep: true };
				}
			} catch (error) {
				toast.error(
					getLitheErrorMessage(
						error,
						`Error confirming ${getLitheStepLabel(
							ugStep,
							LitheCreationTypeEnum.UG
						).toLowerCase()}.`
					)
				);
			}

			return { moveToNextStep: false };
		},
		[isSuccessfulStepResponse, litheId]
	);

	const confirmCustomColumnsChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				const data =
					await services.litheService.confirmCustomColumns(
						litheId
					);

				if (isSuccessfulStepResponse(data)) {
					toast.success(
						"Custom columns confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				}

				return {
					moveToNextStep: false,
				};
			} catch (error) {
				toast.error(
					getLitheErrorMessage(
						error,
						"Error confirming custom columns"
					)
				);
				return {
					moveToNextStep: false,
				};
			}
		}, [isSuccessfulStepResponse, litheId]);

	const confirmTrainingDataChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				const data =
					await services.litheService.confirmTrainingData(
						litheId
					);

				if (isSuccessfulStepResponse(data)) {
					toast.success(
						"Training data confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				}
				return {
					moveToNextStep: false,
				};
			} catch {
				toast.error("Error confirming training data");
				return {
					moveToNextStep: false,
				};
			}
		}, [isSuccessfulStepResponse, litheId]);

	const confirmTransitionConfigChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				const data =
					await services.litheService.confirmTransitionConfig(
						litheId
					);

				if (isSuccessfulStepResponse(data)) {
					toast.success(
						"Transition config confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				}
				return {
					moveToNextStep: false,
				};
			} catch {
				toast.error("Error confirming transition config");
				return {
					moveToNextStep: false,
				};
			}
		}, [isSuccessfulStepResponse, litheId]);

	const confirmReplinesChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			const data =
				await services.litheService.confirmReplines(
					litheId
				);

			if (isSuccessfulStepResponse(data)) {
				toast.success("Replines confirmed successfully");
				return {
					moveToNextStep: true,
				};
			}
			return {
				moveToNextStep: false,
			};
		} catch {
			toast.error("Error confirming replines");
			return {
				moveToNextStep: false,
			};
		}
	}, [isSuccessfulStepResponse, litheId]);

	const confirmCashflowVizChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				const data =
					await services.litheService.confirmCashflowViz(
						litheId
					);

				if (isSuccessfulStepResponse(data)) {
					// Return success from here then call meta and then move to next
					toast.success(
						"Risk Factor confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				} else {
					return {
						moveToNextStep: false,
					};
				}
			} catch {
				toast.error("Error confirming risk factor");
				return {
					moveToNextStep: false,
				};
			}
		}, [isSuccessfulStepResponse, litheId]);

	const confirmBucketSelectionChanges =
		useCallback(async () => {
			try {
				if (!litheId) return;
				const data =
					await services.litheService.confirmProbs(litheId);
				if (isSuccessfulStepResponse(data)) {
					// Return success from here then call meta and then move to next
					toast.success("Buckets confirmed successfully");
					return {
						moveToNextStep: true,
					};
				} else {
					return {
						moveToNextStep: false,
					};
				}
			} catch {
				toast.error("Error confirming buckets");
				return {
					moveToNextStep: false,
				};
			}
		}, [isSuccessfulStepResponse, litheId]);

	const confirmApprovalChanges = useCallback(async () => {
		try {
			if (!litheId) return;
			const data =
				await services.litheService.confirmLitheApprovalInfo(
					litheId,
					litheCreationType === LitheCreationTypeEnum.UG
				);

			if (isSuccessfulStepResponse(data)) {
				toast.success("Approval confirmed successfully");
				return {
					moveToNextStep: true,
				};
			} else {
				return {
					moveToNextStep: false,
				};
			}
		} catch {
			toast.error("Error confirming approval");
			return {
				moveToNextStep: false,
			};
		}
	}, [
		isSuccessfulStepResponse,
		litheCreationType,
		litheId,
	]);

	const confirmLitheRFSpecsChanges =
		useCallback(async () => {
			try {
				if (!litheId) {
					return {
						moveToNextStep: false,
					};
				}

				if (
					litheCreationType === LitheCreationTypeEnum.MANUAL
				) {
					if (incompleteManualRFQuestions.length > 0) {
						toast.error(
							`Complete the required fields before continuing: ${incompleteManualRFQuestions
								.map((question) => question.question)
								.join(", ")}`
						);
						return {
							moveToNextStep: false,
						};
					}

					const confirmResponse =
						await services.litheService.processLitheManualRF(
							litheId,
							true
						);
					const confirmStatus = String(
						confirmResponse?.status ?? ""
					).toUpperCase();

					if (confirmStatus === "ERROR") {
						throw new Error(
							getLitheErrorMessage(
								confirmResponse?.output?.error_msg ??
									confirmResponse?.error_msg,
								"Risk factors could not be confirmed."
							)
						);
					}

					if (
						isSuccessfulStepResponse(confirmResponse) &&
						confirmStatus === "COMPLETED"
					) {
						toast.success(
							"Risk factors confirmed successfully"
						);
						return {
							moveToNextStep: true,
						};
					}

					return {
						moveToNextStep: false,
					};
				}

				if (
					litheCreationType === LitheCreationTypeEnum.UG
				) {
					const confirmResponse =
						await services.litheService.processLitheUgRFSpecs(
							litheId,
							true
						);
					const confirmStatus = String(
						confirmResponse?.status ?? ""
					).toUpperCase();

					if (confirmStatus === "ERROR") {
						throw new Error(
							getLitheErrorMessage(
								confirmResponse?.error_msg,
								"Probability analysis could not be confirmed."
							)
						);
					}

					if (confirmStatus === "COMPLETED") {
						toast.success(
							"Probability analysis confirmed successfully"
						);
						return { moveToNextStep: true };
					}

					return { moveToNextStep: false };
				}

				const pollingStatuses = new Set([
					"IN_PROGRESS",
					"PENDING",
					"QUEUED",
					"STARTED",
					"NOT_YET_STARTED",
				]);

				const waitForRFSpecsCompletion = async () => {
					for (
						let attempt = 0;
						attempt < 24;
						attempt += 1
					) {
						const result =
							await services.litheService.getLitheRFSpecsResults(
								litheId
							);
						const resultStatus = String(
							result?.status ?? ""
						).toUpperCase();

						if (resultStatus === "COMPLETED") {
							return result;
						}

						if (resultStatus === "ERROR") {
							throw new Error(
								getLitheErrorMessage(
									result?.output?.error_msg ??
										result?.error_msg,
									"Model params processing failed."
								)
							);
						}

						if (
							!pollingStatuses.has(resultStatus) ||
							attempt === 23
						) {
							throw new Error(
								"Model params processing timed out."
							);
						}

						await new Promise<void>((resolve) => {
							setTimeout(resolve, 2500);
						});
					}

					throw new Error(
						"Model params processing timed out."
					);
				};

				const initialConfirmResponse =
					await services.litheService.confirmLitheRFSpecs(
						litheId
					);
				let resultStatus = String(
					initialConfirmResponse?.status ?? ""
				).toUpperCase();

				if (resultStatus === "ERROR") {
					throw new Error(
						getLitheErrorMessage(
							initialConfirmResponse?.output?.error_msg ??
								initialConfirmResponse?.error_msg,
							"Model params could not be confirmed."
						)
					);
				}

				if (
					isSuccessfulStepResponse(
						initialConfirmResponse
					) &&
					resultStatus === "COMPLETED"
				) {
					toast.success(
						"Model params confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				}

				const processResponse =
					await services.litheService.processLitheRFSpecsAnalysis(
						litheId
					);
				resultStatus = String(
					processResponse?.status ?? ""
				).toUpperCase();

				if (resultStatus === "ERROR") {
					throw new Error(
						getLitheErrorMessage(
							processResponse?.output?.error_msg ??
								processResponse?.error_msg,
							"Model params processing failed."
						)
					);
				}

				if (pollingStatuses.has(resultStatus)) {
					const completedResult =
						await waitForRFSpecsCompletion();
					resultStatus = String(
						completedResult?.status ?? ""
					).toUpperCase();
				}

				if (resultStatus !== "COMPLETED") {
					throw new Error(
						"Model params processing did not finish successfully."
					);
				}

				const confirmResponse =
					await services.litheService.confirmLitheRFSpecs(
						litheId
					);
				const confirmStatus = String(
					confirmResponse?.status ?? ""
				).toUpperCase();

				if (
					isSuccessfulStepResponse(confirmResponse) &&
					confirmStatus === "COMPLETED"
				) {
					toast.success(
						"Model params confirmed successfully"
					);
					return {
						moveToNextStep: true,
					};
				}

				toast.error("Model params could not be confirmed.");
				return {
					moveToNextStep: false,
				};
			} catch (error) {
				toast.error(
					getLitheErrorMessage(
						error,
						"Error confirming model params."
					)
				);
				return {
					moveToNextStep: false,
				};
			}
		}, [
			incompleteManualRFQuestions,
			isSuccessfulStepResponse,
			litheCreationType,
			litheId,
		]);

	const continueAndConfirmQuestions =
		useCallback(async () => {
			if (
				effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
			) {
				return;
			}

			setContinueAndConfirmLoading(true);
			let res = {
				moveToNextStep: false,
			};

			switch (step) {
				case LitheStepEnum.SPECS: {
					res = (await confirmSpecsChanges()) ?? res;
					break;
				}
				case LitheStepEnum.UGCLEANDATA:
				case LitheStepEnum.UGFILTERS:
				case LitheStepEnum.UGTRANSDEF:
				case LitheStepEnum.UGFEATUREENG:
				case LitheStepEnum.UGREPLINES: {
					res = await confirmUgAnalysisStep(
						step as LitheStepEnum
					);
					break;
				}
				case LitheStepEnum.DATAVIZ: {
					res = (await confirmDataVizChanges()) ?? res;
					break;
				}
				case LitheStepEnum.ENGGFEATURES: {
					res =
						(await confirmCustomColumnsChanges()) ?? res;
					break;
				}
				case LitheStepEnum.TRAININGDATA: {
					res = (await confirmTrainingDataChanges()) ?? res;
					break;
				}
				case LitheStepEnum.TRANSITION: {
					res =
						(await confirmTransitionConfigChanges()) ?? res;
					break;
				}
				case LitheStepEnum.REPLINES: {
					res = (await confirmReplinesChanges()) ?? res;
					break;
				}
				case LitheStepEnum.MODELPARAMS: {
					res = {
						moveToNextStep: true,
					};
					break;
				}
				case LitheStepEnum.CASHFLOWVIZ: {
					res = (await confirmCashflowVizChanges()) ?? res;
					break;
				}
				case LitheStepEnum.PROBS: {
					res = (await processProbsChanges()) ?? res;
					break;
				}
				case LitheStepEnum.APPROVAL: {
					res = (await confirmApprovalChanges()) ?? res;
					break;
				}
				case LitheStepEnum.RFSPECS: {
					res = await confirmLitheRFSpecsChanges();
					break;
				}
				case LitheStepEnum.RAWPROBS: {
					res = (await processRawProbsChanges()) ?? res;
					break;
				}
				default:
					break;
			}

			if (
				litheId &&
				step &&
				step !== LitheStepEnum.LITHEAPPROVED
			) {
				if (res.moveToNextStep) {
					const stepToMoveFrom = get_current_step({
						all_steps: availableLitheSteps,
						route_step: step as LitheStepEnum,
						meta_current_step:
							effectiveCurrentStep ?? undefined,
					});
					const currentIndex =
						availableLitheSteps.indexOf(stepToMoveFrom);
					const nextStep =
						currentIndex >= 0 &&
						currentIndex < availableLitheSteps.length - 1
							? availableLitheSteps[currentIndex + 1]
							: null;

					if (nextStep) {
						dispatch(
							LitheSliceActions.updateLitheMetaCurrentStep(
								nextStep
							)
						);
					} else if (step === LitheStepEnum.APPROVAL) {
						dispatch(
							LitheSliceActions.updateLitheMetaCurrentStep(
								LitheStepEnum.LITHEAPPROVED
							)
						);
					}
					setContinueAndConfirmLoading(false);
					moveToNextStep(step as LitheStepEnum);
				} else {
					await dispatch(fetchLitheThunk(litheId));
					setContinueAndConfirmLoading(false);
				}
			}
		}, [
			availableLitheSteps,
			step,
			litheId,
			effectiveCurrentStep,
			confirmSpecsChanges,
			confirmUgAnalysisStep,
			confirmDataVizChanges,
			confirmCustomColumnsChanges,
			confirmTrainingDataChanges,
			confirmTransitionConfigChanges,
			confirmReplinesChanges,
			confirmApprovalChanges,
			confirmLitheRFSpecsChanges,
			confirmCashflowVizChanges,
			processProbsChanges,
			processRawProbsChanges,
			dispatch,
			moveToNextStep,
		]);

	const runAnalysis = useCallback(
		(step: LitheStepEnum) => {
			if (
				effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
			) {
				return;
			}

			switch (step) {
				case LitheStepEnum.SPECS: {
					break;
				}
				case LitheStepEnum.DATAVIZ: {
					void processDataVizChanges();
					break;
				}
				case LitheStepEnum.CASHFLOWVIZ: {
					void processCashflowVizChanges();
					break;
				}
				case LitheStepEnum.PROBS: {
					processBucketSelectionChanges();
					break;
				}
				default:
					break;
			}
		},
		[
			effectiveCurrentStep,
			processDataVizChanges,
			processCashflowVizChanges,
			processBucketSelectionChanges,
		]
	);

	const resetLitheAllStateAndPolling = useCallback(() => {
		dispatch(LitheSliceActions.resetLitheState());
		dataVizPollingController.current = {};
		cashflowVizPollingController.current = {};
		setStepScreenLoading(false);
		setStepScreenStatus("idle");
		if (dataVizPollingTimeout.current) {
			clearTimeout(dataVizPollingTimeout.current);
			dataVizPollingTimeout.current = null;
		}
		if (cashflowVizPollingTimeout.current) {
			clearTimeout(cashflowVizPollingTimeout.current);
			cashflowVizPollingTimeout.current = null;
		}
		dataVizContinuePolling.current = false;
		cashflowVizContinuePolling.current = false;
	}, [dispatch]);

	const disableContinue = useMemo(() => {
		if (stepScreenLoading) {
			return true;
		}
		const shouldGateOnStepStatus = [
			LitheStepEnum.SPECS,
			LitheStepEnum.UGCLEANDATA,
			LitheStepEnum.UGFILTERS,
			LitheStepEnum.UGTRANSDEF,
			LitheStepEnum.UGFEATUREENG,
			LitheStepEnum.UGREPLINES,
			...(litheCreationType === LitheCreationTypeEnum.UG
				? [LitheStepEnum.RFSPECS]
				: []),
			LitheStepEnum.DATAVIZ,
			LitheStepEnum.ENGGFEATURES,
			LitheStepEnum.TRAININGDATA,
			LitheStepEnum.TRANSITION,
			LitheStepEnum.REPLINES,
			LitheStepEnum.CASHFLOWVIZ,
			LitheStepEnum.MODELPARAMS,
			...(litheCreationType ===
			LitheCreationTypeEnum.DATASET
				? [LitheStepEnum.RFSPECS]
				: []),
		].includes(step as LitheStepEnum);
		if (
			shouldGateOnStepStatus &&
			stepScreenStatus !== "success"
		) {
			return true;
		}
		if (
			effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
		) {
			return true;
		}
		switch (step) {
			case LitheStepEnum.SPECS:
				if (
					litheCreationType === LitheCreationTypeEnum.MANUAL
				) {
					return (
						rfSpecsQuestions.loading ||
						incompleteManualRFQuestions.length > 0
					);
				}
				return incompleteSpecsQuestions.length > 0;
			case LitheStepEnum.UGCLEANDATA:
			case LitheStepEnum.UGFILTERS:
			case LitheStepEnum.UGTRANSDEF:
			case LitheStepEnum.UGFEATUREENG:
			case LitheStepEnum.UGREPLINES:
				return false;
			case LitheStepEnum.DATAVIZ:
				return false;
			case LitheStepEnum.ENGGFEATURES:
				return false;
			case LitheStepEnum.TRAININGDATA:
				return false;
			case LitheStepEnum.TRANSITION:
				return false;
			case LitheStepEnum.REPLINES:
				return false;
			case LitheStepEnum.MODELPARAMS:
				return false;
			case LitheStepEnum.CASHFLOWVIZ:
				return false;
			case LitheStepEnum.PROBS:
				return false;
			case LitheStepEnum.RFSPECS:
				if (
					litheCreationType === LitheCreationTypeEnum.MANUAL
				) {
					return (
						rfSpecsQuestions.loading ||
						incompleteManualRFQuestions.length > 0
					);
				}
				return false;
			case LitheStepEnum.RAWPROBS:
				return rawProbData.loading;
			case LitheStepEnum.APPROVAL:
				return false;
			case LitheStepEnum.LITHEAPPROVED:
				return true;
			default:
				return true;
		}
	}, [
		effectiveCurrentStep,
		incompleteManualRFQuestions.length,
		incompleteSpecsQuestions.length,
		litheCreationType,
		rawProbData.loading,
		rfSpecsQuestions.loading,
		stepScreenLoading,
		stepScreenStatus,
		step,
	]);

	const shouldDisableEverything = useMemo(() => {
		if (isReadOnlyUser) {
			return true;
		}
		// `dataset` is now a legacy creation type. Keep these lithes
		// inspectable, but prevent every step mutation and submission.
		if (
			meta.data?.litheCreationType ===
			LitheCreationTypeEnum.DATASET
		) {
			return true;
		}
		if (
			effectiveCurrentStep === LitheStepEnum.LITHEAPPROVED
		) {
			return true;
		} else if (meta.data === null) {
			return true;
		}
		return false;
	}, [effectiveCurrentStep, isReadOnlyUser, meta.data]);

	// The top-bar action owns its loading state. Step initialization and
	// Run Analysis progress only disable the action; they must not make it
	// appear as though the user clicked Next Step.
	useEffect(() => {
		setContinueAndConfirmLoading(false);
	}, [litheId, step]);

	return (
		<LitheContext.Provider
			value={{
				method,
				step,
				litheId,
				meta,
				allLitheSteps,
				runAnalysis,
				moveToStep,
				triggerDataVizPolling,
				triggerCashflowVizPolling,
				triggerProbsPolling,
				continueAndConfirmQuestions,
				resetLitheAllStateAndPolling,
				showGenerateSummaryModal,
				setShowGenerateSummaryModal,
				confirmBucketSelectionChanges,
				disableContinue,
				shouldDisableEverything,
				isReadOnlyUser,
				stepScreenLoading,
				setStepScreenLoading,
				stepScreenStatus,
				incompleteSpecsQuestions,
				setStepScreenStatus,
				continueAndConfirmLoading,
				setContinueAndConfirmLoading,
				isLitheApproved,
				litheCreationType,
				getProbResultOnDemand,
				moveToNextStep,
			}}
		>
			{children}
		</LitheContext.Provider>
	);
};
